import PropTypes from 'prop-types';
import { Box, Button, Typography, CircularProgress } from '@mui/material';

/**
 * StepNavigation — Bottom navigation bar for stepped advanced forms.
 *
 * - Back button (outlined, left) — hidden on first step
 * - Continue / Sign & Submit button (filled, right)
 * - Acknowledgment counter when toggle_ack fields are incomplete
 * - "Continue" calls onNext; last step calls onSubmit
 */
export default function StepNavigation({
    onBack,
    onNext,
    onSubmit,
    isFirstStep,
    isLastStep,
    remainingAcks,
    isSubmitting,
    theme,
    nextStepName,
    nextFormName,
}) {
    const mobilePagePadding = Math.min(theme?.pagePadding ?? 32, 12);
    const primaryColor = theme?.submitButtonColor || '#212B36';
    const customSubmitText = theme?.submitButtonText;
    // Built-in defaults that we should NOT treat as a practice customisation
    const isDefaultText = !customSubmitText || customSubmitText === 'Submit' || customSubmitText === 'Continue' || customSubmitText === 'Sign & Submit';
    // Determine submit button text based on context
    const getSubmitLabel = () => {
        // If the practice set a custom label (not one of our defaults), always respect it
        if (!isDefaultText) return customSubmitText;
        // If there's a next form in a sequence, signal continuation
        if (nextFormName) return 'Submit & Continue';
        return 'Submit';
    };
    // Step-name suffix shown only on sm+ via the JSX below. On mobile (xs)
    // we render just "Continue" because step names like "Aftercare" wrap to a
    // second line in the narrow flex:1 cell next to Back, making the Continue
    // button visibly taller than Back.
    const submitLabel = getSubmitLabel();
    const stepSuffix = (!isLastStep && nextStepName) ? ` \u2014 ${nextStepName}` : '';
    const btnRadius = theme?.submitButtonBorderRadius ? `${theme.submitButtonBorderRadius}px` : '8px';
    const btnFontSize = theme?.submitButtonFontSize || '15px';
    const btnFontWeight = theme?.submitButtonFontWeight || 600;
    const btnTextColor = theme?.submitButtonTextColor || '#ffffff';

    return (
        <Box sx={{
            mt: 3, mb: 2,
            // Sticky bottom on mobile for easy thumb-reach
            '@media (max-width: 599.95px)': {
                position: 'sticky',
                bottom: 0,
                zIndex: 10,
                bgcolor: 'inherit',
                pt: 1.5,
                pb: 2,
                mx: `-${mobilePagePadding}px`,
                px: `${mobilePagePadding}px`,
                borderTop: '1px solid rgba(145,158,171,0.12)',
                backdropFilter: 'blur(8px)',
                background: 'rgba(255,255,255,0.92)',
            },
        }}>
            {/* Acknowledgment counter */}
            {remainingAcks > 0 && (
                <Typography sx={{
                    textAlign: 'center',
                    fontSize: '13px',
                    color: '#919EAB',
                    mb: 2,
                    fontStyle: 'italic',
                }}>
                    {remainingAcks} acknowledgement{remainingAcks !== 1 ? 's' : ''} remaining
                </Typography>
            )}

            {/* All consents acknowledged — no text shown, validated on submit */}

            <Box sx={{
                display: 'flex',
                gap: 2,
                alignItems: 'center',
                justifyContent: isFirstStep ? 'flex-end' : 'space-between',
            }}>
                {/* Back — hidden on first step */}
                {!isFirstStep && (
                    <Button
                        variant="outlined"
                        onClick={onBack}
                        disabled={isSubmitting}
                        sx={{
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontWeight: 600,
                            px: { xs: 2.5, sm: 3 },
                            py: 1.5,
                            fontSize: '15px',
                            borderColor: '#DFE3E8',
                            color: '#637381',
                            minWidth: { xs: 80, sm: 100 },
                            minHeight: '48px',
                            flex: { xs: 1, sm: 'none' },
                            whiteSpace: 'nowrap',
                            lineHeight: 1.2,
                            '&:hover': {
                                borderColor: '#919EAB',
                                bgcolor: 'transparent',
                            },
                        }}
                    >
                        Back
                    </Button>
                )}

                {/* Continue / Sign & Submit */}
                <Button
                    variant="contained"
                    onClick={isLastStep ? onSubmit : onNext}
                    disabled={isSubmitting}
                    sx={{
                        borderRadius: btnRadius,
                        textTransform: 'none',
                        fontWeight: btnFontWeight,
                        px: { xs: 3, sm: 4 },
                        py: 1.5,
                        bgcolor: primaryColor,
                        color: btnTextColor,
                        fontSize: btnFontSize,
                        minWidth: { xs: 130, sm: 160 },
                        minHeight: '48px',
                        flex: { xs: 1, sm: 'none' },
                        // Keep label on one line so the button height matches
                        // Back exactly. Long step names get truncated on
                        // narrow viewports — preferable to a wrapped, taller
                        // button.
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        lineHeight: 1.2,
                        boxShadow: 'none',
                        letterSpacing: '0.02em',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                            bgcolor: primaryColor,
                            filter: 'brightness(1.15)',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        },
                        '&:active': {
                            filter: 'brightness(0.95)',
                        },
                        '&.Mui-disabled': {
                            bgcolor: `${primaryColor}50`,
                            color: `${btnTextColor}99`,
                            boxShadow: 'none',
                        },
                    }}
                >
                    {isSubmitting ? (
                        <CircularProgress size={22} sx={{ color: btnTextColor }} />
                    ) : (
                        <>
                            {isLastStep ? submitLabel : 'Continue'}
                            {!isLastStep && stepSuffix && (
                                <Box
                                    component="span"
                                    sx={{ display: { xs: 'none', sm: 'inline' } }}
                                >
                                    {stepSuffix}
                                </Box>
                            )}
                        </>
                    )}
                </Button>
            </Box>
        </Box>
    );
}

StepNavigation.propTypes = {
    onBack: PropTypes.func.isRequired,
    onNext: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    isFirstStep: PropTypes.bool.isRequired,
    isLastStep: PropTypes.bool.isRequired,
    remainingAcks: PropTypes.number,
    isSubmitting: PropTypes.bool,
    // eslint-disable-next-line react/forbid-prop-types
    theme: PropTypes.object,
    nextStepName: PropTypes.string,
    nextFormName: PropTypes.string,
};

StepNavigation.defaultProps = {
    remainingAcks: 0,
    isSubmitting: false,
    theme: null,
    nextStepName: '',
    nextFormName: '',
};
